import React, { useContext, useMemo } from "react";
import { MainContext } from "../../context/MainContext";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";

import WifiOutlinedIcon from "@mui/icons-material/WifiOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import BluetoothOutlinedIcon from "@mui/icons-material/BluetoothOutlined";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";

import * as Constants from "../api/constants";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const FIELDS = [
  { id: 0, label: "Command Echo", icon: <SyncOutlinedIcon /> },
  { id: 1, label: "Software Version", icon: <MemoryOutlinedIcon /> },
  { id: 2, label: "BLE MAC Address", icon: <BluetoothOutlinedIcon /> },
  { id: 3, label: "Wi-Fi RSSI", icon: <WifiOutlinedIcon /> },
  { id: 4, label: "Up time: Total", icon: <AccessTimeOutlinedIcon /> },
  { id: 5, label: "Up time: Wi-Fi", icon: <AccessTimeOutlinedIcon /> },
  { id: 6, label: "Up time: BLE", icon: <AccessTimeOutlinedIcon /> },
  { id: 7, label: "Up time: TCP", icon: <AccessTimeOutlinedIcon /> },
  { id: 8, label: "Wi-Fi SSID", icon: <RouterOutlinedIcon /> },
  { id: 9, label: "BSSID enable", icon: <RouterOutlinedIcon /> },
  { id: 10, label: "BSSID MAC Address", icon: <RouterOutlinedIcon /> },
  { id: 11, label: "Wi-Fi Fail Counter", icon: <DnsOutlinedIcon /> },
  { id: 12, label: "TCP Fail Counter", icon: <DnsOutlinedIcon /> },
  { id: 13, label: "Server RF Ping Fail Counter", icon: <DnsOutlinedIcon /> },
  { id: 14, label: "Wi-Fi MAC Address", icon: <WifiOutlinedIcon /> },
  { id: 15, label: "MQTT Server Address", icon: <CloudOutlinedIcon /> },
  { id: 16, label: "MQTT Status", icon: <CloudOutlinedIcon /> },
];

function valueOrNA(arr, i) {
  const v = arr?.[i];
  return v === undefined || v === null || v === "" ? "N/A" : v;
}

function statusChipProps(label, rawValue) {
  // Special styling only for MQTT Status
  if (label !== "MQTT Status") return null;

  const v = String(rawValue ?? "").trim().toLowerCase();

  const isGood =
    v === "connected" || v === "online" || v === "ok" || v === "1" || v === "true";

  const isBad =
    v === "disconnected" || v === "offline" || v === "fail" || v === "0" || v === "false";

  if (!v || v === "n/a") {
    return {
      label: "N/A",
      sx: {
        bgcolor: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "rgba(255,255,255,0.75)",
        fontWeight: 700,
        fontSize: 12,
        height: 22,
      },
    };
  }

  if (isGood) {
    return {
      label: rawValue,
      sx: {
        bgcolor: "rgba(76,175,80,0.15)",
        border: "1px solid rgba(76,175,80,0.45)",
        color: "#4CAF50",
        fontWeight: 800,
        fontSize: 12,
        height: 22,
      },
    };
  }

  if (isBad) {
    return {
      label: rawValue,
      sx: {
        bgcolor: "rgba(244,67,54,0.15)",
        border: "1px solid rgba(244,67,54,0.40)",
        color: "#F44336",
        fontWeight: 800,
        fontSize: 12,
        height: 22,
      },
    };
  }

  // Unknown status – neutral chip
  return {
    label: rawValue,
    sx: {
      bgcolor: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.12)",
      color: "rgba(255,255,255,0.8)",
      fontWeight: 800,
      fontSize: 12,
      height: 22,
    },
  };
}

export default function SsigSnSsigRF() {
  const state = useContext(MainContext).state;
  const deviceId = state.selectedDevice;

  const sn = (state.snSsigRf ?? "").split(",");

  const sendCommand = (command, value) => {
    fetch(Constants.baseURL + Constants.apiSendCommand, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ DeviceId: deviceId, Command: command, Value: value }),
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          toast.success("Command sent successfully. Refreshing...", {
            position: "top-right",
            autoClose: 6000,
          });

          setTimeout(() => state.refreshDeviceRF(deviceId), 3000);
          setTimeout(() => state.getUserDeviceRf(deviceId), 8000);
        } else {
          toast.error("Send failed. Please try again", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      })
      .catch((err) => console.log("request failed", err));
  };

  const onUpdate = () => sendCommand("SN-SSIG-RF", "");

  const cards = useMemo(() => {
    return FIELDS.map((f) => ({
      ...f,
      value: valueOrNA(sn, f.id),
    }));
  }, [sn]);

  return (
    <>
      <ToastContainer />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography component="h2" variant="h6" color="white" sx={{ fontWeight: 600 }}>
          SN-SSIG-RF Data
        </Typography>

        <IconButton
          onClick={onUpdate}
          sx={{
            color: "white",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "50%", 
            width: 40,
            height: 40,
            "&:hover": {
              borderColor: "white",
              backgroundColor: "rgba(255,255,255,0.08)",
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        {cards.map((item) => {
          const chip = statusChipProps(item.label, item.value);

          return (
            <Grid item xs={12} sm={6} md={6} lg={6} key={item.id}>
              <Paper
                sx={{
                  p: 1.4,
                  minHeight: 86,
                  borderRadius: 2,
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  transition: "0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    bgcolor: "rgba(255,255,255,0.08)",
                  },
                }}
              >
                {/* Icon bubble */}
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.10)",
                    color: "rgba(255,255,255,0.75)",
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>

                {/* Label + Value (chip bottom-aligned) */}
                <Box
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>

                  <Box sx={{ mt: 0.8, display: "flex", alignItems: "flex-end" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontWeight: 700,
                        fontSize: 12,
                        wordBreak: "break-word",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
}
